/**
 * AuthImage Component
 * Renders images that may require JWT authentication (e.g. photo proxy URLs).
 *
 * Auto-detects whether a URL needs auth by checking for the photo proxy pattern
 * (`/photos/` + `/image`). CF Images URLs (imagedelivery.net) and other public
 * URLs are rendered as plain <img> tags with no fetch overhead.
 */

import { useState, useEffect, useRef } from 'react';
import { tokenManager } from '../api/client';

export interface AuthImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  onError?: () => void;
  onLoad?: () => void;
}

/** Returns true when the URL needs a Bearer auth fetch (no embedded token) */
function needsAuth(url: string): boolean {
  // Signed URLs with ?token= or &token= don't need Bearer auth — render as plain <img>
  if (url.includes('?token=') || url.includes('&token=')) return false;
  return url.includes('/photos/') && url.includes('/image');
}

export function AuthImage({ src, alt, className, loading = 'eager', onError, onLoad }: AuthImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const isAuthUrl = needsAuth(src);

  useEffect(() => {
    if (failed) onError?.();
  }, [failed, onError]);

  useEffect(() => {
    // Reset state when src changes
    setFailed(false);
    setObjectUrl(null);

    if (!isAuthUrl) return;

    let revoked = false;
    const headers: Record<string, string> = {};
    const token = tokenManager.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(src, { headers })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`${r.status}`))))
      .then((blob) => {
        if (revoked) return;
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setObjectUrl(url);
      })
      .catch(() => {
        if (!revoked) setFailed(true);
      });

    return () => {
      revoked = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src, isAuthUrl]);

  // Non-auth URL: render plain <img>
  if (!isAuthUrl) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        onError={onError}
        onLoad={onLoad}
      />
    );
  }

  // Auth URL still loading
  if (!objectUrl && !failed) {
    return <div className={`${className || ''} bg-gray-200 dark:bg-slate-700 animate-pulse`} />;
  }

  // Auth URL failed
  if (failed) {
    return <div className={`${className || ''} bg-gray-200 dark:bg-slate-700`} />;
  }

  // Auth URL loaded
  return (
    <img
      src={objectUrl!}
      alt={alt}
      className={className}
      loading={loading}
      onError={onError}
      onLoad={onLoad}
    />
  );
}

export default AuthImage;
